/**
 * @module api/interceptors
 * @description Axios 인터셉터 및 인증/에러 공통 유틸 함수 모음.
 *   - 요청 인터셉터: 인증 토큰 주입, 바이패스 토큰 주입, 쓰기 전 자동 토큰 갱신
 *   - 응답 인터셉터: 401/403 세션 만료 처리, 400/500 에러 토스트
 *   - 세션 만료 및 바이패스 토큰 만료 처리 함수
 */
import store from '@/store'
import i18n from '@/locales/i18n'
import jwtDecode from 'jwt-decode'
import { clearConversation } from '@/stores/assistantStorage'

const { t, te } = i18n.global

// Module-level router reference, set during setupInterceptors
let _router = null

// Session expiration flag — prevents duplicate 401 toasts
let _sessionExpired = false

/** 세션 만료 플래그를 초기화합니다. 로그인 성공 후 호출됩니다. */
const resetSessionExpired = () => {
  _sessionExpired = false
}

// SURINP-191 (2026-04-29): popupPaths 배열 + popupPaths.includes() 분기 + window.close() 호출 모두 제거.
// 본래 별창 register popup 이 부모 세션 만료 시 자체 window.close() 하기 위한 메커니즘이었으나,
// S7 정리로 모든 register popup 이 ModalPopup 으로 흡수되어 popup window 자체가 더 이상 존재하지 않음.
// 남은 window.open() 사용처(인쇄 미리보기 / 외부 검색 결과)는 popupPaths 에 등록되지 않았으므로 영향 없음.

/**
 * 앱 전역 알림(토스트)을 표시합니다.
 * window.__APP_NOTIFY__가 없으면 __APP_LOG__로 폴백합니다.
 *
 * @param {string} message - 표시할 메시지
 * @param {'error'|'success'|'warn'} [type='error'] - 알림 타입
 */
const notifyMessage = (message, type = 'error') => {
  if (typeof window !== 'undefined' && typeof window.__APP_NOTIFY__ === 'function') {
    window.__APP_NOTIFY__({ message, type })
    return
  }
  if (typeof window !== 'undefined' && typeof window.__APP_LOG__ === 'function') {
    window.__APP_LOG__(message)
  }
}

/** 에러 응답의 HTTP 상태코드를 반환합니다. */
const getErrorStatus = (error) => error?.response?.status ?? error?.status ?? null
/** 에러 응답의 바디 데이터를 반환합니다. */
const getErrorData = (error) => error?.response?.data ?? error?.data ?? null
/** 에러 응답 바디의 code 필드를 반환합니다. */
const getErrorCode = (error) => getErrorData(error)?.code
/** 401 또는 403 여부를 확인합니다. (바이패스 토큰 처리용 — 세션 처리는 401 만) */
const isUnauthorized = (status) => status === 401 || status === 403

/**
 * 서버 오류 code 의 안내 문구 — `errorMessage.<code>` 키가 있으면 그 번역, 없으면 일반 문구(fallbackKey).
 * 키가 없는 code 가 화면에 「errorMessage.<code>」 원문으로 보이지 않게 한다 (SURINP-887).
 * 현재 언어에 없어도 대체 언어(fallbackLocale)에 있으면 번역으로 본다 — t() 가 대체 언어 문구를 준다.
 *
 * @param {string} code - 응답 바디의 code
 * @param {string} [fallbackKey='message.error'] - 키가 없을 때 쓸 i18n 키
 * @returns {string}
 */
const errorCodeMessage = (code, fallbackKey = 'message.error') => {
  const key = `errorMessage.${code}`
  const fallbackLocale = i18n.global.fallbackLocale
  const has = te(key) || (typeof fallbackLocale === 'string' && te(key, fallbackLocale))
  return has ? t(key) : t(fallbackKey)
}

/**
 * 에러 코드가 있으면 해당 i18n 메시지를, 없으면 기본 메시지를 토스트로 표시합니다.
 *
 * @param {Error} error - Axios 에러 객체
 * @param {string} [fallbackKey='message.error'] - 코드 없을 때 사용할 i18n 키
 */
const notifyErrorCodeOrDefault = (error, fallbackKey = 'message.error') => {
  const code = getErrorCode(error)
  if (code) {
    notifyMessage(errorCodeMessage(code, fallbackKey))
    return
  }
  notifyMessage(t(fallbackKey))
}

/**
 * 세션 기반 인증의 401 에러를 처리합니다. (SURINP-738: 403 은 권한 없음이라 세션을 끊지 않는다)
 * 세션 만료 토스트를 표시하고 로그인 페이지로 리디렉션합니다.
 * 중복 처리를 막기 위해 _sessionExpired 플래그를 사용합니다.
 *
 * @param {Error} error - Axios 에러 객체
 * @returns {boolean} 처리 여부 (true: 401 처리됨, false: 해당 없음)
 */
const handleSessionUnauthorized = (error) => {
  const status = getErrorStatus(error)
  if (status !== 401) {
    return false
  }

  // Already handling session expiration — suppress duplicate toasts and redirects
  if (_sessionExpired) {
    return true
  }
  _sessionExpired = true

  const urlPathName = window.location.pathname
  store.commit('user', {})
  sessionStorage.removeItem('authToken')
  clearConversation() // 어시스턴트 대화 저장분(AA-143) — 인메모리 대화는 다음 로그인의 reset 이 지운다
  notifyMessage(t('message.session'))

  if (urlPathName.startsWith('/mobile/staff-team')) {
    _router.push({ path: '/mobile/staff-team/login' })
  } else {
    _router.push({ path: '/login' })
  }
  return true
}

/**
 * 바이패스 토큰의 401/403 에러를 처리합니다.
 * 세션 리디렉션 없이 토큰 만료 토스트만 표시합니다.
 *
 * @param {Error} error - Axios 에러 객체
 * @returns {boolean} 처리 여부 (true: 401/403 처리됨, false: 해당 없음)
 */
const handleBypassUnauthorized = (error) => {
  const status = getErrorStatus(error)
  if (!isUnauthorized(status)) {
    return false
  }

  const code = getErrorCode(error)
  if (code) {
    notifyMessage(errorCodeMessage(code, 'message.expired'))
  } else {
    notifyMessage(t('message.expired'))
  }
  return true
}

/**
 * JWT 액세스 토큰을 갱신합니다.
 * 현재 경로가 모바일 수리파트너 경로이면 모바일 전용 갱신 URL을 사용합니다.
 * 갱신 성공 시 Vuex 스토어와 sessionStorage를 업데이트합니다.
 *
 * @param {import('axios').AxiosInstance} axiosInstance - CS 백엔드 Axios 인스턴스
 * @returns {Promise<string|false>} 새 authToken 또는 false (403 시 세션 만료 처리)
 * @throws {Error} 403 이외의 갱신 실패 시 에러를 다시 던집니다
 */
const refreshToken = async (axiosInstance) => {
  const authToken = store.state.auth.user?.id?.authToken
  const headers = {
    Authorization: authToken || ''
  }

  try {
    const urlPathName = window.location.pathname
    const refreshUrl = urlPathName.startsWith('/mobile/staff-team')
      ? '/mobile/staff-team/auth/token/refresh'
      : '/auth/token/refresh'

    const response = await axiosInstance.post(refreshUrl, null, {
      headers,
      _skipAuth: true,
      _skipErrorHandler: true
    })

    const authToken = response.data
    const decodedToken = jwtDecode(authToken)
    const userType = decodedToken.userType
    const userGroupId = decodedToken.userGroupId
    const partnerId = store.state.auth.user?.id?.partnerId || ''
    store.commit('user', {
      loginId: store.state.auth.user?.id?.loginId,
      authToken,
      userGroupId,
      userType,
      partnerId
    })
    sessionStorage.setItem('authToken', authToken)

    return authToken
  } catch (error) {
    if (error.response && error.response.status === 403) {
      store.commit('user', {})
      sessionStorage.removeItem('authToken')
      clearConversation() // 어시스턴트 대화 저장분(AA-143)
      return false
    }
    // Clear _skipErrorHandler so the outer interceptor can handle the error properly
    if (error.config) {
      delete error.config._skipErrorHandler
    }
    throw error
  }
}

/** 자동 토큰 갱신 대상 HTTP 메서드 목록 */
const WRITE_METHODS = ['post', 'patch', 'put', 'delete']

/**
 * Axios 인스턴스에 요청/응답 인터셉터를 등록합니다.
 *
 * 요청 인터셉터:
 *   - _skipAuth: 인증 헤더 생략 (noAuthApi용)
 *   - _customerSearch: 공개 진행 조회 — 고정 X-Bypass-Token·Authorization 없이 X-Customer-Search-Token 만 (SURINP-733)
 *   - _useBypass: X-Bypass-Token 주입 (모바일 바이패스용)
 *   - _autoRefresh: 쓰기 메서드 전 토큰 자동 갱신
 *
 * 응답 인터셉터:
 *   - _skipErrorHandler: 에러 처리 생략
 *   - 401/403: 세션 또는 바이패스 만료 처리
 *   - 400: 에러 코드 토스트
 *   - 500: 서버 에러 토스트
 *
 * @param {import('axios').AxiosInstance} axiosInstance - 인터셉터를 등록할 인스턴스
 * @param {import('vue-router').Router} router - Vue Router 인스턴스
 */
const setupInterceptors = (axiosInstance, router) => {
  _router = router

  // --- Request Interceptor ---
  axiosInstance.interceptors.request.use(
    async (config) => {
      // noAuthApi: skip all auth
      if (config._skipAuth) {
        config.withCredentials = false
        return config
      }

      // 공개 진행 조회(SURINP-733): 고정 X-Bypass-Token·Authorization 없이 검색 토큰만 싣는다
      if (config._customerSearch) {
        config.withCredentials = false
        if (config._searchToken) {
          config.headers['X-Customer-Search-Token'] = config._searchToken
        }
        return config
      }

      // bypassGetApi / bypassApi: use X-Bypass-Token
      if (config._useBypass) {
        config.headers['X-Bypass-Token'] = store.state.auth?.bypassToken || ''
        return config
      }

      // Auto-refresh token before write operations
      if (config._autoRefresh && WRITE_METHODS.includes(config.method)) {
        await refreshToken(axiosInstance)
      }

      // Inject Authorization header (if available)
      const authToken = store.state.auth.user?.id?.authToken
      if (authToken) {
        config.headers.Authorization = authToken
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // --- Response Error Interceptor ---
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Skip error handler if flagged
      if (error.config?._skipErrorHandler) {
        return Promise.reject(error)
      }

      const status = getErrorStatus(error)

      // Bypass-specific unauthorized handling
      if (error.config?._useBypass || error.config?._customerSearch) {
        if (handleBypassUnauthorized(error)) {
          error._handled = true
          return Promise.reject(error)
        }
      } else {
        // Session unauthorized handling
        if (handleSessionUnauthorized(error)) {
          error._handled = true
          return Promise.reject(error)
        }
      }

      // 403: 권한 없음 안내만 — 로그아웃하지 않는다 (SURINP-738)
      if (status === 403 && !error.config?._useBypass && !error.config?._customerSearch) {
        notifyMessage(t('message.forbidden'))
        error._handled = true
        return Promise.reject(error)
      }

      // 400: notify error code or default message
      if (status === 400) {
        // 호출부가 스스로 처리(재시도·안내)하겠다고 한 코드는 토스트를 띄우지 않는다 (SURINP-893)
        const silent = error.config?._silentErrorCodes
        if (!(Array.isArray(silent) && silent.includes(getErrorCode(error)))) notifyErrorCodeOrDefault(error)
        error._handled = true
        return Promise.reject(error)
      }

      // 500: server error toast
      if (status === 500) {
        const method = (error.config?.method || '').toLowerCase()
        const messageKey = method === 'get' ? 'message.error' : 'message.saved400'
        notifyMessage(t(messageKey))
        error._handled = true
        return Promise.reject(error)
      }

      // Other errors: generic error toast
      notifyMessage(t('message.error'))
      error._handled = true
      return Promise.reject(error)
    }
  )
}

export {
  setupInterceptors,
  notifyMessage,
  getErrorStatus,
  getErrorData,
  getErrorCode,
  notifyErrorCodeOrDefault,
  errorCodeMessage,
  handleSessionUnauthorized,
  handleBypassUnauthorized,
  refreshToken,
  resetSessionExpired
}
